import { useState, useCallback } from 'react'

export const useForm = (initialValues = {}) => {
  const [values, setValues] = useState(initialValues)
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Snapshot of the last loaded/saved values, to know if the form is dirty.
  const [baseline, setBaseline] = useState(() => JSON.stringify(initialValues))
  const isDirty = JSON.stringify(values) !== baseline

  const setValue = useCallback((name, value) => {
    setValues(prev => ({ ...prev, [name]: value }))
    // Typing in a field clears its error — feedback stays fresh.
    setErrors(prev => (prev[name] ? { ...prev, [name]: '' } : prev))
  }, [])

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target
    const fieldValue = type === 'checkbox' ? checked : value
    setValue(name, fieldValue)
  }, [setValue])

  const setError = useCallback((name, error) => {
    setErrors(prev => ({ ...prev, [name]: error }))
  }, [])

  const replaceErrors = useCallback((nextErrors) => {
    setErrors(nextErrors || {})
  }, [])

  const clearErrors = useCallback(() => setErrors({}), [])

  const handleSubmit = useCallback(async (onSubmit) => {
    setIsSubmitting(true)
    try {
      await onSubmit(values)
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }, [values])

  const reset = useCallback(() => {
    setValues(initialValues)
    setBaseline(JSON.stringify(initialValues))
    setErrors({})
    setIsSubmitting(false)
  }, [initialValues])

  const setInitialValues = useCallback((newValues) => {
    setValues(newValues)
    setBaseline(JSON.stringify(newValues))
  }, [])

  return {
    values,
    errors,
    isDirty,
    isSubmitting,
    handleChange,
    handleSubmit,
    setValue,
    setValues,
    setError,
    setErrors: replaceErrors,
    clearErrors,
    reset,
    setInitialValues
  }
}
